import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  BarController,
  LineController,
  Tooltip
} from 'chart.js';
import LoadingComponent from '@/components/common/LoadingComponent';
import ErrorState from '@/components/common/ErrorStateComponent';
import HeadingComponent from '../shared/heading';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  BarController,
  LineController,
  Tooltip
);

function resolveLabelOverlap(chart) {
  if (!chart || !chart.ctx) return;

  const ctx = chart.ctx;
  const fontSize = 10;

  const barDatasets = chart.data.datasets
    .map((ds, i) => ({ ds, i }))
    .filter(({ ds }) => ds.type !== 'line');

  const totalBars = barDatasets.length;

  barDatasets.forEach(({ ds, i: datasetIndex }, barIdx) => {
    const meta = chart.getDatasetMeta(datasetIndex);
    if (!meta || !meta.data) return;

    meta.data.forEach((point, i) => {
      const value = ds.data[i];
      if (value == null || value === 0) return;

      const isNegative = value < 0;
      const absValue = Math.abs(value);
      const text = isNegative
        ? `(${absValue.toLocaleString()})`
        : absValue.toLocaleString();

      ctx.font = `${fontSize}px sans-serif`;

      const yPosition = isNegative
        ? point.base + Math.abs(point.y - point.base) + 40
        : point.y - 10;

      // ✅ Bar ki actual width nikalo
      const barWidth = point.width || 10;
      // ✅ Har bar ko uski position pe center karo — same jaise bar render hoti hai
      const offset = totalBars > 1
        ? (barIdx - (totalBars - 1) / 2) * (barWidth + 2)
        : 0;
      const labelX = point.x + offset;

      ctx.save();
      ctx.translate(labelX, yPosition - 15);
      ctx.rotate(-Math.PI / 2);
      ctx.fillStyle = isNegative ? '#dc2626' : '#404040';

      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 0, 0);
      ctx.restore();
    });
  });
}

function drawZeroLine(chart) {
  const { ctx, chartArea, scales } = chart;
  if (!scales?.y || !chartArea) return;

  const yZero = scales.y.getPixelForValue(0);
  if (yZero < chartArea.top || yZero > chartArea.bottom) return;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(chartArea.left, yZero);
  ctx.lineTo(chartArea.right, yZero);
  ctx.strokeStyle = '#cccccc';
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
  ctx.stroke();
  ctx.restore();
}

const highlightVersion = (text, version) => {
  if (!text || !version) return text;

  const versionMap = {
    "variance_to_budget": "Variance to Budget",
    "variance_to_forecast": "Variance to Forecast",
  };

  const displayVersion = versionMap[version] || version;
  const regex = new RegExp(`(${displayVersion})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, i) =>
    regex.test(part)
      ? <span key={i} className="text-red-600">{part}</span>
      : part
  );
};

const RevenueChart = ({
  title,
  subTitle,
  graphTitle,
  data = [],
  loading = false,
  error = null,
  datasetsConfig = [],
  tableConfig = [],
  yAxisMin = null,
  yAxisMax = null,
  customLegend = null,
  centerBaseline = false,
  year = null,
  version = ""
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || loading || error || data.length === 0) return;

    const ctx = canvasRef.current.getContext('2d');

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const chartData = {
      labels: data.map(d => d.date),
      datasets: datasetsConfig.map(config => ({
        type: config.type || 'bar',
        label: config.label,
        data: data.map(d => d[config.dataKey]),
        backgroundColor: config.color,
        borderColor: config.color,
        borderWidth: config.type === 'line' ? 1 : 0,
        borderDash: config.borderDash || [],
        pointRadius: 0,
        spanGaps: true,
        barPercentage: 0.4,
        categoryPercentage: 0.5,
        order: config.type === 'line' ? 0 : 1
      }))
    };

    const allBarValues = [];
    datasetsConfig.forEach(config => {
      if (config.type !== 'line') {
        data.forEach(d => {
          const val = d[config.dataKey];
          if (val != null && !isNaN(val)) allBarValues.push(Number(val));
        });
      }
    });

    const hasPositive = allBarValues.some(v => v > 0);
    const hasNegative = allBarValues.some(v => v < 0);

    const shouldCenterBaseline = centerBaseline && hasPositive && hasNegative;

    let calculatedMin = yAxisMin;
    let calculatedMax = yAxisMax;

    if (shouldCenterBaseline) {
      const dataMin = Math.min(...allBarValues, 0);
      const dataMax = Math.max(...allBarValues, 0);
      const absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax));
      calculatedMin = yAxisMin !== null ? yAxisMin : -(absMax * 1.4);
      calculatedMax = yAxisMax !== null ? yAxisMax : (absMax * 1.4);
    }

    // ✅ Label text ki actual width naapo taake bade numbers (zyada digits) ke
    // liye padding bhi utni hi barhe — warna rotated label canvas edge se cut ho jata hai.
    ctx.font = '10px sans-serif';
    let maxPositiveLabelWidth = 0;
    let maxNegativeLabelWidth = 0;
    allBarValues.forEach(v => {
      const text = v < 0
        ? `(${Math.abs(v).toLocaleString()})`
        : Math.abs(v).toLocaleString();
      const width = ctx.measureText(text).width;
      if (v < 0) {
        maxNegativeLabelWidth = Math.max(maxNegativeLabelWidth, width);
      } else {
        maxPositiveLabelWidth = Math.max(maxPositiveLabelWidth, width);
      }
    });

    const topPadding = maxPositiveLabelWidth > 0
      ? Math.max(70, maxPositiveLabelWidth / 2 + 45)
      : 70;
    const bottomPadding = maxNegativeLabelWidth > 0
      ? Math.max(30, maxNegativeLabelWidth / 2 + 65)
      : 30;

    const options = {
      responsive: true,
  maintainAspectRatio: false,
  devicePixelRatio: 2,
      layout: {
        padding: { top: topPadding, bottom: bottomPadding, left: 2, right: 2 }
      },
      scales: {
        x: {
          display: false,
          offset: true,
          grid: { display: false }
        },
        y: {
          display: false,
          min: calculatedMin,
          max: calculatedMax,
          grid: { display: false }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }
      },
      animation: {
        onComplete: () => {
          if (chartRef.current) {
            if (shouldCenterBaseline) drawZeroLine(chartRef.current);
            resolveLabelOverlap(chartRef.current);
          }
        },
        onProgress: () => {
          if (chartRef.current) {
            if (shouldCenterBaseline) drawZeroLine(chartRef.current);
            resolveLabelOverlap(chartRef.current);
          }
        }
      }
    };

    chartRef.current = new ChartJS(ctx, {
      data: chartData,
      options
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data, datasetsConfig, loading, error, yAxisMin, yAxisMax, centerBaseline, year]);

  if (loading) return <LoadingComponent />;
  if (error) return <ErrorState error={error} />;

  return (
    <div className="w-full py-6 px-4">
      <div className="w-full mx-auto px-6 lg:px-8 py-8 rounded-lg shadow-lg bg-card">

        <div className="flex flex-col gap-1 mb-1">
          {title && (
            <HeadingComponent variant={"main"}>
              {title}
            </HeadingComponent>
          )}
          {subTitle && (
            <HeadingComponent variant={"sub"}>
              {highlightVersion(subTitle, version)}
            </HeadingComponent>
          )}
        </div>

        <div className="p-4 w-full !border-2 !border-!border rounded">
          <div className="text-center mb-3 pb-2">
            <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider">
              {graphTitle}
            </h3>
          </div>

          {customLegend && <div className="mb-4">{customLegend}</div>}

          {/* Chart row — label gutter (110px) beside the canvas, matched only in width.
              Row alignment for the table below is handled natively by the table itself,
              not by hand-tuned pixel offsets. */}
          <div className="flex w-full">
            <div className="flex-shrink-0" style={{ width: '110px', height: '384px' }} />
            <div className="flex-1 min-w-0 relative" style={{ height: '384px' }}>
              <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
            </div>
          </div>

          {/* Single table for labels + data so every row's height/borders are
              guaranteed to line up — no separate DOM tree to keep in sync. */}
          <table className="mt-0 w-full !border-collapse" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '110px' }} />
              {data.map((_, i) => <col key={i} />)}
            </colgroup>
            <tbody>
              <tr>
                <td className="bg-card" />
                {data.map((d, i) => (
                  <td key={i} className="py-1 px-1 text-center text-[7px] !border-x !border-t !border-!border bg-card text-foreground">
                    {d.date}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="bg-card" />
                {data.map((d, i) => (
                  <td key={i} className="py-1 px-1 text-center text-[7px] !border-x !border-!border bg-card text-foreground">
                    {year || d.year || '-'}
                  </td>
                ))}
              </tr>

              <tr>
                <td className="bg-card" />
                {data.map((d, i) => (
                  <td key={i} className="py-1 px-1 text-center text-[7px] font-bold !border-x !border-b !border-!border bg-card text-foreground">
                    {d.day}
                  </td>
                ))}
              </tr>

              {tableConfig.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  <td className="!border-t !border-b !border-l">
                    <div className="flex items-center gap-1 py-1 px-2 text-[7px] font-semibold whitespace-nowrap overflow-hidden text-foreground">
                      {row.type === 'line' ? (
                        <div className="flex items-center flex-shrink-0" style={{ width: 16, height: 8, position: 'relative' }}>
                          <svg width="16" height="8" viewBox="0 0 16 8">
                            <line x1="0" y1="4" x2="16" y2="4" stroke={row.color} strokeWidth="1.5" strokeDasharray="3,2" />
                            <circle cx="8" cy="4" r="2.5" fill="white" stroke={row.color} strokeWidth="1.5" />
                          </svg>
                        </div>
                      ) : (
                        <div
                          className="flex-shrink-0"
                          style={{ width: 16, height: 8, backgroundColor: row.color, borderRadius: 2 }}
                        />
                      )}
                      <span className="overflow-hidden text-ellipsis">{row.label}</span>
                    </div>
                  </td>
                  {data.map((d, i) => {
                    const val = d[row.dataKey];
                    let displayValue;
                    const isNegative = val != null && val < 0;

                    if (row.formatter) {
                      displayValue = row.formatter(val);
                    } else if (val == null) {
                      displayValue = '0';
                    } else if (val < 0) {
                      displayValue = `(${Math.abs(val).toLocaleString()})`;
                    } else {
                      displayValue = val.toLocaleString();
                    }

                    return (
                      <td key={i} className="py-1 text-center text-[7px] font-medium !border !border-!border bg-card">
                        <span className={isNegative ? 'text-red-600' : 'text-foreground'}>
                          {displayValue}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;